import type { ProjectView } from "@portal/schemas";
import { Button, Input, NativeSelect, NativeSelectOption } from "@portal/ui";
import { Pin, Plus, Search } from "lucide-react";
import { useDeferredValue, useState } from "react";
import { usePrefetchMemo, useProjectMemos } from "../../api/queries";
import { CardStatus } from "../../components/cards/CardStatus";
import { HomeSection } from "../../components/HomeSection";
import { MemoDialog } from "../memos/MemoDialog";
import { MemoHoverPreview } from "../memos/MemoHoverPreview";
import { MemoIcon } from "../memos/MemoIcon";
import { MemoRowMenu } from "../memos/MemoRowMenu";

const MEMOS_HINT =
  "このプロジェクトのメモです。全員が読めて、参加者は誰でも作成・編集・ボードへのピン留めができます。アーカイブと削除は作った人だけです。";

const updated = new Intl.DateTimeFormat("ja-JP", {
  month: "numeric",
  day: "numeric",
  timeZone: "Asia/Tokyo",
});

/**
 * プロジェクトのメモの一覧(SPEC §13.6)。ホームのメモとは別で、全員に見える。
 * 検索(サーバー側)とアーカイブの表示、行の「⋮」メニュー(開く・ボードに留める・アーカイブ・削除)を持つ。
 */
export function ProjectMemosCard({
  project,
  className,
}: {
  project: ProjectView;
  className?: string;
}) {
  const [q, setQ] = useState("");
  const [scope, setScope] = useState<"all" | "archived">("all");
  const query = useDeferredValue(q.trim());
  const memos = useProjectMemos(project.id, {
    ...(query ? { q: query } : {}),
    ...(scope === "archived" ? { scope } : {}),
  });
  const prefetch = usePrefetchMemo();
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const canCreate = memos.data?.canCreate ?? false;
  const rows = memos.data?.memos ?? [];

  return (
    <HomeSection
      data-testid="project-memos-card"
      className={className}
      title="メモ"
      description={MEMOS_HINT}
      action={
        canCreate && (
          <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
            <Plus />
            メモを追加
          </Button>
        )
      }
    >
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            aria-label="メモを検索"
            placeholder="メモを検索"
            className="pl-8"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <NativeSelect
          aria-label="表示するメモ"
          value={scope}
          onChange={(e) => setScope(e.target.value as "all" | "archived")}
        >
          <NativeSelectOption value="all">すべて</NativeSelectOption>
          <NativeSelectOption value="archived">アーカイブ</NativeSelectOption>
        </NativeSelect>
      </div>
      <CardStatus
        isPending={memos.isPending}
        isError={memos.isError}
        refetch={() => memos.refetch()}
      />
      {memos.data && rows.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {query
            ? "見つかりませんでした。"
            : scope === "archived"
              ? "アーカイブしたメモはありません。"
              : "メモはまだありません。"}
        </p>
      )}
      <ul className="flex flex-col text-sm">
        {rows.map((m) => (
          <li key={m.id} className="group flex items-center gap-1 hover:bg-accent">
            <MemoHoverPreview title={m.title || "無題のメモ"} icon={m.icon} memoId={m.id}>
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-2 px-1 py-2 text-left"
                onClick={() => setOpenId(m.id)}
                onPointerEnter={() => void prefetch(m.id)}
                onFocus={() => void prefetch(m.id)}
              >
                <MemoIcon value={m.icon} />
                <span className="truncate">{m.title || "無題のメモ"}</span>
                {m.pinnedAt !== null && (
                  <Pin
                    className="size-3.5 shrink-0 text-muted-foreground"
                    aria-label="ボードに留めています"
                  />
                )}
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {updated.format(new Date(m.updatedAt))}
                </span>
              </button>
            </MemoHoverPreview>
            <MemoRowMenu
              memo={m}
              onOpen={() => setOpenId(m.id)}
              canPin={canCreate || m.role === "OWNER"}
            />
          </li>
        ))}
      </ul>
      {memos.data?.truncated && (
        <p className="text-xs text-muted-foreground">新しい 100 件だけ出しています。</p>
      )}
      <MemoDialog
        memoId={openId}
        draftId={null}
        open={openId !== null}
        onOpenChange={(o) => !o && setOpenId(null)}
      />
      <MemoDialog
        memoId={null}
        // プロジェクトのメモの新規は下書きを残さない(ホームの下書きの一覧に混ざるため)
        draftId={null}
        projectId={project.id}
        open={creating}
        onOpenChange={setCreating}
      />
    </HomeSection>
  );
}
